// src/pages/Dashboard.js
import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import Menu from "../components/Menu";
import "./dashboard.css";

const TZ = "America/Sao_Paulo";

const formatDateTimeBR = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: TZ,
  });
};

const relativeWhen = (iso) => {
  if (!iso) return "";
  const now = new Date();
  const target = new Date(iso);
  if (isNaN(target.getTime())) return "";
  const diffMs = target.getTime() - now.getTime();
  const abs = Math.abs(diffMs);
  const mins = Math.round(abs / (60 * 1000));
  const hours = Math.round(abs / (60 * 60 * 1000));
  const days = Math.round(abs / (24 * 60 * 60 * 1000));
  const prefix = diffMs >= 0 ? "em" : "há";
  if (mins < 60) return `${prefix} ${mins} min`;
  if (hours < 24) return `${prefix} ${hours} h`;
  return `${prefix} ${days} dia${days > 1 ? "s" : ""}`;
};

function computeSessionState(session) {
  if (!session || !session.scheduled_at) {
    return { code: "none", label: "Sem sessão", className: "badge--none" };
  }
  const now = new Date();
  const start = new Date(session.scheduled_at);
  const end = new Date(start.getTime() + 90 * 60 * 1000); // 1h30 fallback
  const raw = (session.status || "").toLowerCase();

  if (raw === "canceled" || raw === "cancelada") {
    return { code: "canceled", label: "Cancelada", className: "badge--canceled" };
  }
  if (now >= start && now <= end && raw !== "closed") {
    return { code: "live", label: "Em andamento", className: "badge--live" };
  }
  if (now > end && raw !== "open") {
    return { code: "finished", label: "Encerrada", className: "badge--finished" };
  }
  if (raw === "open") return { code: "open", label: "Aberta", className: "badge--open" };
  if (raw === "closed") return { code: "closed", label: "Fechada", className: "badge--closed" };
  if (raw === "scheduled" || raw === "agendada") {
    return { code: "scheduled", label: "Agendada", className: "badge--scheduled" };
  }
  if (now < start) return { code: "scheduled", label: "Agendada", className: "badge--scheduled" };
  return { code: "unknown", label: "Status indisponível", className: "badge--unknown" };
}

const EmptyState = ({ title, subtitle }) => (
  <div className="empty-state">
    <div className="empty-emoji">⚽</div>
    <div className="empty-title">{title}</div>
    {subtitle && <div className="empty-subtitle">{subtitle}</div>}
  </div>
);

const Dashboard = () => {
  const [adminGroups, setAdminGroups] = useState([]);
  const [memberGroups, setMemberGroups] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("authToken");
        const headers = { Authorization: `Bearer ${token}` };

        const adminRes = await api.get("/api/groups/admin", { headers });
        const adminWithSession = await Promise.all(
          (adminRes.data || []).map(async (g) => {
            try {
              const s = await api.get(`/api/groups/${g.id}/next-session`, { headers });
              return { ...g, session: s.data || null };
            } catch {
              return { ...g, session: null };
            }
          })
        );
        setAdminGroups(adminWithSession);

        const memberRes = await api.get("/api/groups/member", { headers });
        const memberWithSession = await Promise.all(
          (memberRes.data || []).map(async (g) => {
            try {
              const s = await api.get(`/api/groups/${g.id}/next-session`, { headers });
              return { ...g, session: s.data || null };
            } catch {
              return { ...g, session: null };
            }
          })
        );
        setMemberGroups(memberWithSession);
      } catch (err) {
        setError(err?.response?.data?.error || "Erro ao carregar dados.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totalAdmin = useMemo(() => adminGroups.length, [adminGroups]);
  const totalMember = useMemo(() => memberGroups.length, [memberGroups]);

  const handleDetails = (groupId) => {
    window.location.href = `/peladas/${groupId}`;
  };
  const handleAdmin = (groupId) => {
    window.location.href = `/admin/${groupId}`;
  };

  return (
    <div className="dashboard">
      {/* Header fixo (Menu com saudação/editar) */}
      <div className="topbar">
        <Menu />
      </div>

      {loading && (
        <div className="loader-overlay">
          <div className="loader-spinner"></div>
          <p className="loader-text">Aguarde, carregando...</p>
        </div>
      )}

      <div className="dashboard-content">
        {error && <p className="error-message">{error}</p>}

        {/* KPIs */}
        <header className="dash-header">
          <div>
            <h1 className="dash-title">Painel</h1>
            <p className="dash-subtitle">Acompanhe seus grupos e próximas peladas</p>
          </div>
          <div className="dash-kpis">
            <div className="kpi-card">
              <div className="kpi-label">Meus grupos (admin)</div>
              <div className="kpi-value">{totalAdmin}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Meus grupos (membro)</div>
              <div className="kpi-value">{totalMember}</div>
            </div>
          </div>
        </header>

        <div className="dashboard-grid">
          {/* Administro */}
          <section className="dashboard-card">
            <div className="card-head">
              <h2>Grupos que administro</h2>
            </div>
            {adminGroups.length > 0 ? (
              <div className="cards-container">
                {adminGroups.map((g) => {
                  const s = g.session || {};
                  const st = computeSessionState(s);
                  return (
                    <div key={g.id} className="pelada-card">
                      <div className="pelada-top">
                        <h3 className="pelada-title">{s.place_name || g.name || "—"}</h3>
                        <span className={`badge ${st.className}`}>{st.label}</span>
                      </div>

                      <div className="pelada-info">
                        <div className="info-row">
                          <span className="info-label">Quando</span>
                          <span className="info-value">
                            {formatDateTimeBR(s.scheduled_at)}
                            <span className="muted"> {relativeWhen(s.scheduled_at)}</span>
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Endereço</span>
                          <span className="info-value">{s.place_address || "—"}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Confirmados</span>
                          <span className="info-value strong">{s.confirmed_count ?? 0}</span>
                        </div>
                      </div>

                      <div className="button-group">
                        <button className="btn btn--ghost" onClick={() => handleDetails(g.id)}>
                          Mais informações
                        </button>
                        <button className="btn btn--primary" onClick={() => handleAdmin(g.id)}>
                          Administrar
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState
                title="Nenhum grupo administrado"
                subtitle="Peça acesso de admin ou crie um novo grupo."
              />
            )}
          </section>

          {/* Participo */}
          <section className="dashboard-card">
            <div className="card-head">
              <h2>Grupos que participo</h2>
            </div>
            {memberGroups.length > 0 ? (
              <div className="cards-container">
                {memberGroups.map((g) => {
                  const s = g.session || {};
                  const st = computeSessionState(s);
                  return (
                    <div key={g.id} className="pelada-card">
                      <div className="pelada-top">
                        <h3 className="pelada-title">{s.place_name || g.name || "—"}</h3>
                        <span className={`badge ${st.className}`}>{st.label}</span>
                      </div>

                      <div className="pelada-info">
                        <div className="info-row">
                          <span className="info-label">Quando</span>
                          <span className="info-value">
                            {formatDateTimeBR(s.scheduled_at)}
                            <span className="muted"> {relativeWhen(s.scheduled_at)}</span>
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Endereço</span>
                          <span className="info-value">{s.place_address || "—"}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Confirmados</span>
                          <span className="info-value strong">{s.confirmed_count ?? 0}</span>
                        </div>
                      </div>

                      <div className="button-group">
                        <button className="btn btn--primary" onClick={() => handleDetails(g.id)}>
                          Mais informações
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState
                title="Você ainda não participa de grupos"
                subtitle="Entre em um grupo para ver as próximas peladas."
              />
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
